import React, { useState, useEffect, useCallback, useRef } from 'react';
import '../../styles/sections/ImageProcessor.css';
import { loadStripe } from '@stripe/stripe-js';
import PricingSection from './PricingSection';

function ImageProcessor() {
  const [originalImage, setOriginalImage] = useState(null);
  const [processedImage, setProcessedImage] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isExpressShipping, setIsExpressShipping] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [couponCode, setCouponCode] = useState('');
  
  // Image position and scale
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [imageScale, setImageScale] = useState(1);
  const [dragStart, setDragStart] = useState(null);

  const [isResizing, setIsResizing] = useState(false);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, scale: 1 });

  const basePrice = 19.99;  // Price per tile in AUD
  const expressShippingPrice = 5.00;  // Express shipping cost
  const standardShippingPrice = 0;  // Standard shipping is free
  const pickupDiscount = 10.00;  // Pickup discount amount

  // Add new state for coupon discount
  const [couponDiscount, setCouponDiscount] = useState(0);

  // Add this state to store the processed image data
  const [processedImageData, setProcessedImageData] = useState(null);

  // Add these state variables at the top with your other useState declarations
  const [grayscale, setGrayscale] = useState(100);
  const [brightness, setBrightness] = useState(100);

  // Add new state for raw file
  const [rawImageFile, setRawImageFile] = useState(null);

  // Add state to track touch points for pinch-to-zoom
  const [touchPoints, setTouchPoints] = useState([]);
  const [initialDistance, setInitialDistance] = useState(null);
  const [initialScale, setInitialScale] = useState(1);

  // Improved pinch-to-zoom implementation
  const [lastTouchDistance, setLastTouchDistance] = useState(null);

  const calculateTotal = () => {
    const subtotal = basePrice * quantity;
    const shipping = isExpressShipping ? expressShippingPrice : standardShippingPrice;
    return Math.max(0, subtotal + shipping - couponDiscount);
  };

  useEffect(() => {
    if (originalImage) {
      processImage();
    }
  }, [originalImage]); // Only run when image changes

  // Add this function after the useState declarations
  const resizeImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 1200;
          const MAX_HEIGHT = 1200;
          
          let width = img.width;
          let height = img.height;
          
          // Calculate new dimensions while maintaining aspect ratio
          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to JPEG with 0.8 quality to reduce file size
          const resizedDataUrl = canvas.toDataURL('image/jpeg', 0.8);
          resolve(resizedDataUrl);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setRawImageFile(file);
      const resizedDataUrl = await resizeImage(file);
      setOriginalImage(resizedDataUrl);
      processImage(resizedDataUrl);
    }
  };

  const processImage = (sourceImage = originalImage) => {
    if (!sourceImage) return;

    const img = new Image();
    img.src = sourceImage;
    
    img.onload = () => {
      // Create a square tile canvas
      const canvas = document.createElement('canvas');
      const TILE_SIZE = 1000; // Fixed tile size
      canvas.width = TILE_SIZE;
      canvas.height = TILE_SIZE;
      const ctx = canvas.getContext('2d');

      // Fill with white background
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, TILE_SIZE, TILE_SIZE);

      // Draw the image centered
      ctx.drawImage(img, 0, 0, TILE_SIZE, TILE_SIZE);
      
      // Apply greyscale first
      const imageData = ctx.getImageData(0, 0, TILE_SIZE, TILE_SIZE);
      const data = imageData.data;

      // First pass: convert to grayscale
      for (let i = 0; i < data.length; i += 4) {
        const avg = (data[i] + data[i + 1] + data[i + 2]) / 3;
        data[i] = avg;     // R
        data[i + 1] = avg; // G
        data[i + 2] = avg; // B
      }

      // Second pass: apply fade to white based on tile position
      for (let y = 0; y < TILE_SIZE; y++) {
        for (let x = 0; x < TILE_SIZE; x++) {
          const i = (y * TILE_SIZE + x) * 4;
          
          // Calculate distance from center of tile (normalized to 0-1)
          const centerX = TILE_SIZE / 2;
          const centerY = TILE_SIZE / 2;
          const distanceX = Math.abs(x - centerX) / centerX;
          const distanceY = Math.abs(y - centerY) / centerY;
          
          // Use the maximum of X and Y distance for square fade
          const normalizedDistance = Math.max(distanceX, distanceY);
          
          // Create fade effect (stronger at edges)
          const fadeStart = 0.9; // Start fade at 90% from center
          const fadeStrength = normalizedDistance < fadeStart ? 1 : 
            Math.max(0, 1 - Math.pow((normalizedDistance - fadeStart) / (1 - fadeStart), 2));
          
          // Get current grayscale value
          const gray = data[i];
          
          // Fade to white
          const fadeToWhite = gray + (255 - gray) * (1 - fadeStrength);
          
          // Apply fade
          data[i] = fadeToWhite;     // R
          data[i + 1] = fadeToWhite; // G
          data[i + 2] = fadeToWhite; // B
        }
      }
      
      ctx.putImageData(imageData, 0, 0);

      // Convert to BMP
      const bmpDataUrl = canvas.toDataURL('image/bmp');
      setProcessedImage(bmpDataUrl);
      setProcessedImageData(bmpDataUrl);
    };
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    setDragStart({
      x: e.clientX - imagePosition.x,
      y: e.clientY - imagePosition.y
    });
    setIsDragging(true);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    setImagePosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      setRawImageFile(file);
      const resizedDataUrl = await resizeImage(file);
      setOriginalImage(resizedDataUrl);
      processImage(resizedDataUrl);
    }
  };

  const handleEmptyTileClick = () => {
    document.getElementById('file-upload').click();
  };

  const handleRemoveImage = () => {
    setOriginalImage(null);
    setProcessedImage(null);
    setImagePosition({ x: 0, y: 0 });
    setImageScale(1);
    
    // Reset the file input
    const fileInput = document.getElementById('file-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleResizeStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      scale: imageScale
    });
  };

  const handleResizeMove = (e) => {
    if (!isResizing) return;
    
    const deltaX = e.clientX - resizeStart.x;
    const deltaY = e.clientY - resizeStart.y;
    const delta = Math.max(deltaX, deltaY) * 0.01;
    
    const newScale = Math.max(0.1, Math.min(5, resizeStart.scale + delta));
    setImageScale(newScale);
  };

  const handleResizeEnd = () => {
    setIsResizing(false);
  };

  const handleApplyCoupon = () => {
    if (couponCode.toLowerCase() === 'pickup') {
      setCouponDiscount(pickupDiscount);
      setCouponCode('');
    } else {
      setCouponDiscount(0);
      setCouponCode('');
    }
  };

  // Improved touch handling for mobile
  const handleTouchStart = (e) => {
    if (!originalImage) return;
    
    if (e.touches.length === 2) {
      // Pinch-to-zoom (two fingers)
      e.preventDefault();
      
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      
      setLastTouchDistance(distance);
      setInitialScale(imageScale);
      setIsResizing(true);
      setIsDragging(false);
    } else if (e.touches.length === 1) {
      // Dragging (one finger)
      const touch = e.touches[0];
      setDragStart({
        x: touch.clientX - imagePosition.x,
        y: touch.clientY - imagePosition.y
      });
      setIsDragging(true);
      setIsResizing(false);
    }
  };

  const handleTouchMove = (e) => {
    if (!originalImage) return;
    
    if (e.touches.length === 2 && lastTouchDistance !== null) {
      // Handle pinch-to-zoom
      e.preventDefault();
      
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      
      const newDistance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      
      // Calculate scale change
      const scaleFactor = newDistance / lastTouchDistance;
      const newScale = Math.max(0.1, Math.min(5, imageScale * scaleFactor));
      
      setImageScale(newScale);
      setLastTouchDistance(newDistance);
    } else if (e.touches.length === 1 && isDragging) {
      // Handle dragging
      const touch = e.touches[0];
      setImagePosition({
        x: touch.clientX - dragStart.x,
        y: touch.clientY - dragStart.y
      });
    }
  };

  const handleTouchEnd = (e) => {
    if (e.touches.length < 2) {
      setIsResizing(false);
      setLastTouchDistance(null);
    }
    
    if (e.touches.length === 0) {
      setIsDragging(false);
    } else if (e.touches.length === 1) {
      // Continue dragging with one finger
      const touch = e.touches[0];
      setDragStart({
        x: touch.clientX - imagePosition.x,
        y: touch.clientY - imagePosition.y
      });
      setIsDragging(true);
    }
  };

  // Use a ref to attach touch event listeners
  const tilePreviewRef = useRef(null);
  
  useEffect(() => {
    const element = tilePreviewRef.current;
    if (!element || !originalImage) return;
    
    // Add touch event listeners
    element.addEventListener('touchstart', handleTouchStart, { passive: false });
    element.addEventListener('touchmove', handleTouchMove, { passive: false });
    element.addEventListener('touchend', handleTouchEnd);
    element.addEventListener('touchcancel', handleTouchEnd);
    
    return () => {
      // Clean up event listeners
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
      element.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [originalImage, isDragging, isResizing, imageScale, imagePosition, dragStart, lastTouchDistance]);

  const handleResizeTouchStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const touch = e.touches[0];
    setIsResizing(true);
    setResizeStart({
      x: touch.clientX,
      y: touch.clientY,
      scale: imageScale
    });
  };

  const handleResizeTouchMove = (e) => {
    if (!isResizing) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const touch = e.touches[0];
    const deltaX = touch.clientX - resizeStart.x;
    const deltaY = touch.clientY - resizeStart.y;
    const delta = Math.max(deltaX, deltaY) * 0.01;
    
    const newScale = Math.max(0.1, Math.min(5, resizeStart.scale + delta));
    setImageScale(newScale);
  };

  const handleResizeTouchEnd = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsResizing(false);
  };

  useEffect(() => {
    if (isResizing) {
      // Add mouse event listeners
      window.addEventListener('mousemove', handleResizeMove);
      window.addEventListener('mouseup', handleResizeEnd);
      
      // Add touch event listeners
      window.addEventListener('touchmove', handleResizeTouchMove, { passive: false });
      window.addEventListener('touchend', handleResizeTouchEnd);
      
      return () => {
        // Remove mouse event listeners
        window.removeEventListener('mousemove', handleResizeMove);
        window.removeEventListener('mouseup', handleResizeEnd);
        
        // Remove touch event listeners
        window.removeEventListener('touchmove', handleResizeTouchMove);
        window.removeEventListener('touchend', handleResizeTouchEnd);
      };
    }
  }, [isResizing]);

  // Calculate distance between two touch points
  const getDistance = (p1, p2) => {
    return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
  };

  // Handle multi-touch start for pinch-to-zoom
  const handleMultiTouchStart = (e) => {
    if (!originalImage) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    if (e.touches.length === 2) {
      // Store the two touch points
      const distance = getDistance(
        { x: e.touches[0].clientX, y: e.touches[0].clientY },
        { x: e.touches[1].clientX, y: e.touches[1].clientY }
      );
      
      setLastTouchDistance(distance);
      setInitialScale(imageScale);
      
      // We're now resizing, not dragging
      setIsDragging(false);
      setIsResizing(true);
    } else if (e.touches.length === 1) {
      // Single touch - handle as drag
      const touch = e.touches[0];
      setDragStart({
        x: touch.clientX - imagePosition.x,
        y: touch.clientY - imagePosition.y
      });
      setIsDragging(true);
      setIsResizing(false);
    }
  };

  // Handle multi-touch move for pinch-to-zoom
  const handleMultiTouchMove = (e) => {
    if (!originalImage) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    if (e.touches.length === 2 && lastTouchDistance !== null) {
      // Calculate current distance
      const currentDistance = getDistance(
        { x: e.touches[0].clientX, y: e.touches[0].clientY },
        { x: e.touches[1].clientX, y: e.touches[1].clientY }
      );
      
      // Calculate scale factor based on the change in distance
      const scaleFactor = currentDistance / lastTouchDistance;
      
      // Apply new scale (with limits)
      const newScale = Math.max(0.1, Math.min(5, imageScale * scaleFactor));
      setImageScale(newScale);
      
      // Update last touch distance for next move event
      setLastTouchDistance(currentDistance);
    } else if (e.touches.length === 1 && isDragging) {
      // Single touch - handle as drag
      const touch = e.touches[0];
      setImagePosition({
        x: touch.clientX - dragStart.x,
        y: touch.clientY - dragStart.y
      });
    }
  };

  // Handle multi-touch end
  const handleMultiTouchEnd = (e) => {
    if (!originalImage) return;
    
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Reset touch state
    setLastTouchDistance(null);
    
    // If we still have one finger down, continue dragging
    if (e && e.touches && e.touches.length === 1) {
      const touch = e.touches[0];
      setDragStart({
        x: touch.clientX - imagePosition.x,
        y: touch.clientY - imagePosition.y
      });
      setIsDragging(true);
      setIsResizing(false);
    } else {
      setIsDragging(false);
      setIsResizing(false);
    }
  };

  // Update the tile preview ref useEffect to use our multi-touch handlers
  useEffect(() => {
    const element = tilePreviewRef.current;
    if (!element || !originalImage) return;

    // Use multi-touch handlers that handle both pinch and drag
    const touchStartHandler = (e) => handleMultiTouchStart(e);
    const touchMoveHandler = (e) => handleMultiTouchMove(e);
    const touchEndHandler = (e) => handleMultiTouchEnd(e);
    
    element.addEventListener('touchstart', touchStartHandler, { passive: false });
    element.addEventListener('touchmove', touchMoveHandler, { passive: false });
    element.addEventListener('touchend', touchEndHandler, { passive: false });
    element.addEventListener('touchcancel', touchEndHandler, { passive: false });

    return () => {
      element.removeEventListener('touchstart', touchStartHandler);
      element.removeEventListener('touchmove', touchMoveHandler);
      element.removeEventListener('touchend', touchEndHandler);
      element.removeEventListener('touchcancel', touchEndHandler);
    };
  }, [originalImage, isDragging, isResizing, imageScale, imagePosition, dragStart, lastTouchDistance]);

  const handleCheckout = async () => {
    try {
      console.log('Starting checkout process...');
      
      // Function to create canvas and draw image
      const createCanvasImage = async (sourceImage, greyScale) => {
        const canvas = document.createElement('canvas');
        canvas.width = 300;
        canvas.height = 300;
        const ctx = canvas.getContext('2d');

        // Fill with white background
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Create image
        const img = new Image();
        await new Promise((resolve) => {
          img.onload = resolve;
          img.src = sourceImage;
        });

        // Calculate initial scaling
        let initialScale;
        if (img.width > img.height) {
          initialScale = canvas.width / img.width;
        } else {
          initialScale = canvas.height / img.height;
        }

        // For processed image: apply user scaling and position
        const finalScale = initialScale * imageScale;
        ctx.drawImage(
          img,
          imagePosition.x,
          imagePosition.y,
          img.width * finalScale,
          img.height * finalScale
        );

        // Apply grayscale if needed
        if (greyScale) {
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const data = imageData.data;
          
          for (let i = 0; i < data.length; i += 4) {
            // Skip if it's white background (R, G, and B all 255)
            if (data[i] === 255 && data[i + 1] === 255 && data[i + 2] === 255) continue;
            
            const avg = (data[i] + data[i + 1] + data[i + 2]) / 3;
            data[i] = data[i + 1] = data[i + 2] = avg;
          }
          
          ctx.putImageData(imageData, 0, 0);
        }

        return canvas.toDataURL('image/jpeg');
      };

      // Create canvas images
      const originalDataUrl = await createCanvasImage(originalImage, false);
      const processedDataUrl = await createCanvasImage(originalImage, true);

      // Convert images to blobs
      const originalBlob = await fetch(originalDataUrl).then(r => r.blob());
      const processedBlob = await fetch(processedDataUrl).then(r => r.blob());
      const rawBlob = await fetch(originalImage).then(r => r.blob());

      const formData = new FormData();
      formData.append('originalImage', originalBlob, 'original.jpg');
      formData.append('processedImage', processedBlob, 'processed.jpg');
      formData.append('rawImage', rawBlob, 'raw.jpg');
      formData.append('quantity', quantity.toString());
      formData.append('isExpressShipping', isExpressShipping.toString());
      formData.append('couponDiscount', couponDiscount.toString());

      const response = await fetch(`${process.env.REACT_APP_API_URL}/create-checkout-session`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error('Checkout error response:', errorData);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const { sessionId } = await response.json();
      const stripe = await loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
      
      // Only pass sessionId to redirectToCheckout
      const result = await stripe.redirectToCheckout({ sessionId });
      
      if (result.error) {
        throw new Error(result.error.message);
      }
    } catch (error) {
      console.error('Detailed checkout error:', error);
      alert(`Checkout error: ${error.message}`);
    }
  };

  return (
    <section className="image-processor">
      <div className="processor-container">
        <div className="tile-preview-section">
          <div 
            className={`tile-upload-area ${isDragging ? 'dragging' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="tile-preview-container mobile-optimized">
              {originalImage && (
                <button 
                  className="remove-image-button"
                  onClick={handleRemoveImage}
                  title="Remove image"
                >
                  ×
                </button>
              )}
              <div 
                ref={tilePreviewRef}
                className={`tile-preview ${!originalImage ? 'empty' : ''}`}
                onMouseDown={originalImage ? handleMouseDown : undefined}
                onMouseMove={originalImage ? handleMouseMove : undefined}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onClick={!originalImage ? handleEmptyTileClick : undefined}
                style={originalImage ? {
                  backgroundImage: `url(${processedImage || originalImage})`,
                  backgroundSize: `${imageScale * 100}%`,
                  backgroundPosition: `${imagePosition.x}px ${imagePosition.y}px`,
                  cursor: isResizing ? 'nwse-resize' : 'move'
                } : {}}
              >
                {!originalImage && <p>Click or drag image here</p>}
                {originalImage && (
                  <>
                    <div className="dpi-grid" />
                    <div 
                      className="resize-handle desktop-only"
                      onMouseDown={handleResizeStart}
                    />
                  </>
                )}
              </div>
              
              <div className="dimension-bottom dimension-indicator">
                {window.innerWidth <= 768 ? '100mm × 100mm' : '100mm'}
              </div>
              <div className="dimension-right dimension-indicator">100mm</div>
            </div>
          </div>

          {!originalImage && (
            <div className="upload-controls">
              <label className="upload-button">
                Upload Image
                <input 
                  id="file-upload"
                  type="file" 
                  accept="image/*" 
                  onChange={handleImageUpload} 
                  hidden 
                />
              </label>
              <p className="upload-hint">or drag and drop your image here</p>
            </div>
          )}

          {originalImage && (
            <>
              <div className="zoom-info">
                Drag corner handle to resize • Click and drag to position
              </div>

              <div className="preview-controls">
                <button 
                  className={`preview-button ${!processedImage ? 'active' : ''}`}
                  onClick={() => setProcessedImage(null)}
                >
                  Original
                </button>
                <button 
                  className={`preview-button ${processedImage ? 'active' : ''}`}
                  onClick={() => processImage(originalImage)}
                >
                  Tile View
                </button>
              </div>
            </>
          )}
        </div>

        <PricingSection
          quantity={quantity}
          setQuantity={setQuantity}
          isExpressShipping={isExpressShipping}
          setIsExpressShipping={setIsExpressShipping}
          couponCode={couponCode}
          setCouponCode={setCouponCode}
          couponDiscount={couponDiscount}
          setCouponDiscount={setCouponDiscount}
          originalImage={originalImage}
          processedImage={processedImage}
          imagePosition={imagePosition}
          imageScale={imageScale}
          rawImageFile={rawImageFile}
        />
      </div>
    </section>
  );
}

export default ImageProcessor; 